import { selectedPaymentKind, isCheckCashPaymentKind } from './shared/payment-kind.js';
import { ccEntrySyncManualCcState } from './components/credit-card-entry.js';
import * as FieldValidation from './shared/field-validation.js';
import { renderSavedCards, refreshSavedCardScroll, clearSavedCardSelection } from './shared/saved-card-picker.js';
import { getOverlayElement, showOverlay, hideOverlay } from './shared/overlay.js';
import { showAlertMsg } from './manager/show-alert-msg.js';
import { bindCardSwipeButton, unbindCardSwipeButton } from './shared/card-swipe-button.js';
import { bindPayButtonKeyboardGuard } from './shared/payment-pay-button.js';

const MODAL_ID = 'PayInstallmentsModal';
const MAX_DEFAULT = 72;
const TOKENIZE_TIMEOUT_MS = 30000;

function parseConfig() {
    const el = document.getElementById('pay-installments-modal-config');
    if (!el?.textContent) {
        return null;
    }
    return JSON.parse(el.textContent);
}

function formatMoney(amount) {
    return Number(amount).toFixed(2);
}

function readInt($el) {
    const n = parseInt(String($el.val() || ''), 10);
    return Number.isFinite(n) ? n : 0;
}

function readFloat($el) {
    const n = parseFloat(String($el.val() || ''));
    return Number.isFinite(n) ? n : 0;
}

function savedCardCount($modal) {
    const picker = $modal.find('[data-saved-card-picker="pay_installments_saved_payment_id"]')[0];
    if (!picker) {
        return 0;
    }
    return picker.querySelectorAll('.saved-card-chip[data-saved-card-id]').length;
}

function syncMethodPanels($modal) {
    const kind = selectedPaymentKind($modal, 'payment_method');
    const cash = isCheckCashPaymentKind(kind);
    $modal.find('#pay_installments_cash_wrap').prop('hidden', !cash);
    $modal.find('#pay_installments_card_wrap').toggle(!cash);
    $modal.find('[data-pay-installments-method-pill]').each(function () {
        const $pill = $(this);
        const val = String($pill.data('payInstallmentsMethodPill') || '');
        const active = val === kind;
        $pill.toggleClass('is-active', active);
        $pill.attr('aria-pressed', active ? 'true' : 'false');
    });
    const cardsKnown = $modal.data('payInstallmentsCardsKnown') === true;
    const savedId = String($modal.find('#pay_installments_saved_payment_id').val() || '').trim();
    const showManual = cardsKnown && !cash && (savedCardCount($modal) === 0 || savedId === '');
    $modal.find('[data-manual-fields-collapse]').toggleClass('is-open', showManual);
    ccEntrySyncManualCcState({
        idPrefix: 'pay_installments',
        manualEnabled: showManual,
        requireBaseManualFields: showManual,
        requireZipAndCvv: showManual,
    });
    if (!cash) {
        refreshSavedCardScroll($modal);
    }
    updatePreview($modal);
}

function updatePreview($modal) {
    const cap = Math.max(1, readInt($modal.find('#pay_installments_cap')));
    const indefinite = $modal.find('#pay_installments_indefinite').val() === '1';
    const numLeft = readInt($modal.find('#pay_installments_num_left'));
    const installment = readFloat($modal.find('#pay_installments_installment'));
    const remaining = readFloat($modal.find('#pay_installments_remaining'));
    const hasSurcharge = $modal.find('#pay_installments_has_surcharge').val() === '1';
    let n = readInt($modal.find('#pay_installments_count'));
    if (n < 1) {
        n = 1;
    }
    if (n > cap) {
        n = cap;
        $modal.find('#pay_installments_count').val(String(n));
    }

    const payingAll = !indefinite && n === numLeft;
    const principal = payingAll ? remaining : installment * n;
    const remainingAfter = indefinite ? remaining : Math.max(remaining - principal, 0);
    const countAfter = indefinite ? 'Ongoing' : String(numLeft - n);

    $modal.find('#pay_inst_now_remaining').text(formatMoney(remaining));
    $modal.find('#pay_inst_now_count').text(indefinite ? 'Ongoing' : String(numLeft));
    $modal.find('#pay_inst_now_each').text(formatMoney(installment));
    $modal.find('#pay_inst_after_remaining').text(formatMoney(remainingAfter));
    $modal.find('#pay_inst_after_count').text(countAfter);
    $modal.find('#pay_inst_after_each').text(formatMoney(installment));
    $modal.toggleClass('is-ongoing', indefinite);
    $modal.find('#pay_inst_now_remaining').closest('.edit-recurring-modal__row-owed').prop('hidden', indefinite);
    const $after = $modal.find('#pay_inst_after_remaining').closest('article');
    $after.find('.edit-recurring-modal__row-owed').prop('hidden', payingAll || indefinite);
    $after.find('.edit-recurring-modal__row-how').prop('hidden', payingAll);
    $after.find('#pay_inst_after_complete').prop('hidden', !payingAll);
    const chargeLabel = hasSurcharge
        ? 'Charge $' + formatMoney(principal) + ' plus processing fee'
        : 'Charge $' + formatMoney(principal);
    $modal.find('#pay_installments_charge_note').text(chargeLabel);
    const cash = isCheckCashPaymentKind(selectedPaymentKind($modal, 'payment_method'));
    $modal.find('#pay_installments_submit').text(cash ? 'Save' : 'Pay $' + formatMoney(principal));
}

function showFormError($modal, message) {
    const $err = $modal.find('#pay_installments_form_error');
    $err.text(message).prop('hidden', false).focus();
}

function hideFormError($modal) {
    $modal.find('#pay_installments_form_error').prop('hidden', true).text('');
}

function fetchNonce(tokenUrl, gatewayApi, body) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), TOKENIZE_TIMEOUT_MS);
    return fetch(tokenUrl, {
        method: 'POST',
        headers: {
            Authorization: 'Basic ' + btoa(gatewayApi + ':'),
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller.signal,
    })
        .then((response) => response.json())
        .then((data) => {
            if (data.nonce_token === undefined) {
                return { ok: false, error: data.error_message || 'Card error' };
            }
            return { ok: true, nonce: data.nonce_token };
        })
        .catch((err) => ({
            ok: false,
            error: err && err.name === 'AbortError'
                ? 'Card tokenization timed out. Please try again.'
                : 'Could not tokenize card.',
        }))
        .finally(() => window.clearTimeout(timer));
}

function buildPaymentPayload($modal, cfg) {
    const n = readInt($modal.find('#pay_installments_count'));
    const cap = readInt($modal.find('#pay_installments_cap'));
    if (n < 1 || n > cap) {
        showFormError($modal, 'Choose between 1 and ' + cap + ' installment(s).');
        return null;
    }
    return {
        _token: cfg.csrfToken,
        item_id: $modal.find('#pay_installments_item_id').val(),
        item_type: $modal.find('#pay_installments_item_type').val(),
        installment_count: n,
        payment_method: selectedPaymentKind($modal, 'payment_method'),
        user_id: $modal.find('#pay_installments_user_id').val(),
        mgr_set_paid_notes: $modal.find('#pay_installments_notes').val(),
        send_paid_receipt: $modal.find('#pay_installments_send_receipt').prop('checked') ? 1 : 0,
    };
}

function isInstallmentSubmitLocked($modal) {
    return $modal.data('payInstallmentsSubmitting') === true;
}

function lockInstallmentSubmit($modal) {
    if (isInstallmentSubmitLocked($modal)) {
        return false;
    }
    $modal.data('payInstallmentsSubmitting', true);
    $modal.find('#pay_installments_submit').prop('disabled', true);
    return true;
}

function unlockInstallmentSubmit($modal) {
    $modal.data('payInstallmentsSubmitting', false);
    $modal.find('#pay_installments_submit').prop('disabled', false);
}

function postInstallmentPayment($modal, cfg, onPaid, data) {
    showOverlay(getOverlayElement(), { immediate: true });
    $.ajax({
        url: cfg.submitUrl,
        type: 'POST',
        data,
        success(res) {
            hideOverlay(getOverlayElement(), { immediate: true });
            if (!res || !res.success) {
                unlockInstallmentSubmit($modal);
                showFormError($modal, (res && (res.error || res.msg)) || 'Payment failed.');
                return;
            }
            $modal.modal('hide');
            if (typeof onPaid === 'function') {
                onPaid(res);
            } else {
                showAlertMsg(res.msg || 'Installment payment received.');
            }
        },
        error() {
            hideOverlay(getOverlayElement(), { immediate: true });
            unlockInstallmentSubmit($modal);
            showFormError($modal, 'Payment failed. Please try again.');
        },
    });
}

function swipeElements($modal) {
    return {
        wrap: $modal.find('[data-card-swipe]').get(0),
        button: $modal.find('[data-card-swipe-button]').get(0),
    };
}

function bindInstallmentSwipe($modal, cfg, onPaid) {
    const { wrap, button } = swipeElements($modal);
    const gatewayId = String($modal.data('gatewayId') || '');
    if (!wrap || !button) {
        return;
    }
    bindCardSwipeButton({
        wrap,
        button,
        gatewayJavascriptUrl: (cfg.gatewayJavascriptUrlsById || {})[gatewayId],
        gatewayApi: (cfg.gatewayApisById || {})[gatewayId],
        onSwipe(result) {
            hideFormError($modal);
            if (!lockInstallmentSubmit($modal)) {
                return;
            }
            const payload = buildPaymentPayload($modal, cfg);
            if (!payload) {
                unlockInstallmentSubmit($modal);
                return;
            }
            if (!result.nonce) {
                unlockInstallmentSubmit($modal);
                showFormError($modal, 'The swiped card could not be read. Please try again.');
                return;
            }
            postInstallmentPayment($modal, cfg, onPaid, Object.assign(payload, {
                payment_type: 'new',
                process_type: 'nonce',
                type: 'swipe',
                is_swipe: true,
                nonces: { [gatewayId]: result.nonce },
                name: result.name,
                maskedCard: result.maskedCard,
                cardType: result.cardType,
                last4: result.last4,
                expiryMonth: result.expiryMonth,
                expiryYear: result.expiryYear,
            }));
        },
        onError: (message) => showFormError($modal, message),
    });
}

export function openPayInstallmentsModal($btn) {
    const cfg = parseConfig();
    if (!cfg) {
        throw new Error('openPayInstallmentsModal: config is required');
    }
    const $modal = $('#' + MODAL_ID);
    if (!$modal.length) {
        throw new Error('openPayInstallmentsModal: modal is required');
    }
    hideFormError($modal);
    unlockInstallmentSubmit($modal);
    const itemId = String($btn.attr('data-pledge-id') || '').trim();
    const tableName = String($btn.attr('data-table-name') || 'pledge');
    const itemType = String($btn.attr('data-pledge-type') || 'Pledge');
    const $row = $btn.closest('.clickable-row').length
        ? $btn.closest('.clickable-row')
        : $btn.closest('.data-grid-item');
    const numLeft = parseInt(String($row.attr('data-num-left') || '0'), 10) || 0;
    const indefinite = $row.attr('data-ongoing-indefinitely') === '1';
    const cap = indefinite ? (cfg.maxInstallments || MAX_DEFAULT) : Math.min(numLeft, cfg.maxInstallments || MAX_DEFAULT);
    $modal.find('#pay_installments_item_id').val(itemId);
    $modal.find('#pay_installments_item_type').val(itemType);
    $modal.find('#pay_installments_table_name').val(tableName);
    $modal.find('#pay_installments_user_id').val($row.attr('data-user-id') || '');
    $modal.find('#pay_installments_cap').val(String(Math.max(cap, 1)));
    $modal.find('#pay_installments_indefinite').val(indefinite ? '1' : '0');
    $modal.find('#pay_installments_num_left').val(String(numLeft));
    $modal.find('#pay_installments_installment').val($row.attr('data-installment-amount') || '0');
    $modal.find('#pay_installments_remaining').val($row.attr('data-balance-due') || '0');
    $modal.find('#pay_installments_has_surcharge').val(parseFloat($row.attr('data-surcharge-amount') || '0') > 0 ? '1' : '0');
    $modal.find('#pay_installments_count').attr('max', String(Math.max(cap, 1))).val('1');
    $modal.data('gatewayId', String($row.attr('data-gateway-id') || ''));
    $modal.find('input[name="payment_method"][value="online"]').prop('checked', true);
    $modal.find('#pay_installments_notes').val('');
    $modal.find('#pay_installments_send_receipt').prop('checked', true);
    $modal.data('payInstallmentsCardsKnown', false);
    syncMethodPanels($modal);
    updatePreview($modal);
    const userId = String($row.attr('data-user-id') || '').trim();
    if (userId && cfg.memberContextUrl) {
        fetch(cfg.memberContextUrl + '/' + encodeURIComponent(userId), {
            headers: { 'X-CSRF-TOKEN': cfg.csrfToken },
        })
            .then((r) => r.json())
            .then((payload) => {
                const cards = payload?.saved_cards || payload?.savedCards || [];
                const picker = $modal.find('[data-saved-card-picker="pay_installments_saved_payment_id"]')[0];
                if (picker) {
                    $modal.data('payInstallmentsCardsKnown', true);
                    renderSavedCards($(picker), Array.isArray(cards) ? cards : []);
                    $modal.find('.mgr-pay-method-saved').toggle(Array.isArray(cards) && cards.length > 0);
                }
                syncMethodPanels($modal);
            })
            .catch(() => {
                $modal.data('payInstallmentsCardsKnown', true);
                syncMethodPanels($modal);
            });
    } else {
        $modal.data('payInstallmentsCardsKnown', true);
        syncMethodPanels($modal);
    }
    $modal.modal('show');
}

export function initPayInstallmentsModal(onPaid) {
    const cfg = parseConfig();
    if (!cfg || !document.getElementById(MODAL_ID)) {
        return;
    }
    const $modal = $('#' + MODAL_ID);
    $modal.data('surchargePercent', cfg.surchargePercent || 0);

    $(document).off('click.payInstallmentsPills', '#PayInstallmentsModal [data-pay-installments-method-pill]');
    $(document).on('click.payInstallmentsPills', '#PayInstallmentsModal [data-pay-installments-method-pill]', function (e) {
        e.preventDefault();
        const val = String($(this).data('payInstallmentsMethodPill') || '');
        $modal.find(`input[name="payment_method"][value="${val}"]`).prop('checked', true).trigger('change');
    });

    $modal.off('shown.bs.modal.payInstallmentsSwipe').on('shown.bs.modal.payInstallmentsSwipe', function () {
        bindInstallmentSwipe($modal, cfg, onPaid);
    });
    $modal.off('hidden.bs.modal.payInstallmentsSwipe').on('hidden.bs.modal.payInstallmentsSwipe', function () {
        const { wrap, button } = swipeElements($modal);
        if (wrap && button) {
            unbindCardSwipeButton(button, wrap);
        }
    });

    $modal.off('change.payInstallmentsKind').on('change.payInstallmentsKind', 'input[name="payment_method"]', function () {
        syncMethodPanels($modal);
    });
    $modal.off('input.payInstallmentsCount').on('input.payInstallmentsCount', '#pay_installments_count', function () {
        updatePreview($modal);
    });
    $modal.off('change.payInstallmentsSaved').on('change.payInstallmentsSaved', '#pay_installments_saved_payment_id', function () {
        syncMethodPanels($modal);
    });

    const submitButton = $modal.find('#pay_installments_submit').get(0);
    bindPayButtonKeyboardGuard(submitButton);

    document.addEventListener('saved-card:add-new', function (event) {
        const picker = event.detail && event.detail.picker;
        if (!picker || picker.getAttribute('data-saved-card-picker') !== 'pay_installments_saved_payment_id') {
            return;
        }
        clearSavedCardSelection(picker);
        syncMethodPanels($modal);
    });

    $modal.off('submit.payInstallments').on('submit.payInstallments', '#payInstallmentsForm', function (e) {
        e.preventDefault();
        if (!lockInstallmentSubmit($modal)) {
            return;
        }
        hideFormError($modal);
        const payload = buildPaymentPayload($modal, cfg);
        if (!payload) {
            unlockInstallmentSubmit($modal);
            return;
        }
        const kind = payload.payment_method;

        function postPayment(extra) {
            postInstallmentPayment($modal, cfg, onPaid, Object.assign(payload, extra || {}));
        }

        if (isCheckCashPaymentKind(kind)) {
            postPayment({});
            return;
        }

        const savedId = String($modal.find('#pay_installments_saved_payment_id').val() || '').trim();
        if (savedId) {
            postPayment({ payment_type: 'saved', cc_id: savedId, process_type: 'token' });
            return;
        }

        FieldValidation.clearValidity($modal.find(
            '#pay_installments_name, #pay_installments_card, #pay_installments_expiry_month, #pay_installments_expiry_year, #pay_installments_cvv2, #pay_installments_avs_zip'
        ));
        const name = String($modal.find('#pay_installments_name').val() || '').trim();
        if (!name) {
            unlockInstallmentSubmit($modal);
            showFormError($modal, FieldValidation.messages.card.name);
            return;
        }
        const valid = FieldValidation.validateCardPaymentFields($modal, {
            card: '#pay_installments_card',
            cvv: '#pay_installments_cvv2',
            expiryMonth: '#pay_installments_expiry_month',
            expiryYear: '#pay_installments_expiry_year',
            postalCode: '#pay_installments_avs_zip',
        }, { requireCreditFields: true });
        if (valid !== true) {
            unlockInstallmentSubmit($modal);
            showFormError($modal, 'Please check the card fields.');
            return;
        }

        const gatewayId = String($modal.data('gatewayId') || '');
        const gatewayApi = (cfg.gatewayApisById || {})[gatewayId];
        if (!cfg.tokenUrl || !gatewayApi) {
            unlockInstallmentSubmit($modal);
            showFormError($modal, 'Card processing is not configured.');
            return;
        }
        showOverlay(getOverlayElement(), { immediate: true });
        fetchNonce(cfg.tokenUrl, gatewayApi, {
            card: String($modal.find('#pay_installments_card').val() || '').replace(/ /g, ''),
            cvv2: String($modal.find('#pay_installments_cvv2').val() || '').replace(/_/g, ''),
        }).then((token) => {
            if (!token.ok) {
                hideOverlay(getOverlayElement(), { immediate: true });
                unlockInstallmentSubmit($modal);
                showFormError($modal, token.error || 'Could not tokenize card.');
                return;
            }
            postPayment({
                payment_type: 'new',
                process_type: 'nonce',
                nonce: token.nonce,
                name,
                expiryMonth: $modal.find('#pay_installments_expiry_month').val(),
                expiryYear: FieldValidation.fullExpiryYear($modal.find('#pay_installments_expiry_year').val()),
                avs_zip: $modal.find('#pay_installments_avs_zip').val(),
            });
        });
    });
}
